import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button, FormControlLabel, Switch, useMediaQuery, useTheme } from '@mui/material';
import CustomDialog from '@ui/components/common/Dialog';
import { useEffect } from 'react';
import BDrawer from '@ui/components/common/BDrawer';
import Grid from '@mui/material/Grid2';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import { Icon } from '@iconify/react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ImageUpload from '@ui/pages/Users/AddEditUser/components/ImageUpload';
import { PhoneInputMasking } from '@ui/components/PhoneMasking/PhoneInputMasking';
import { Alert } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useAppDispatch } from '@application/store/useStore';
import { createPartnerContactPersonAsync, updatePartnerContactPersonAsync } from '@application/features/partnerContactPerson/partnerContactPersonThunk';
import { toast } from 'react-toastify';
import { hasPermission } from '@application/helpers/permission-helper';
import { useSelector } from 'react-redux';
const NewContactDialogDrawer = ({ open, onClose, isEdit, partnerId, initialContact }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useAppDispatch();
    // Permission checks
    const loggedInUserPermissions = useSelector((state) => state.auth.currentUser?.permissions || []);
    const canUpdatePartners = hasPermission(loggedInUserPermissions, 'Permissions.Partner.Update');
    const { control, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: {
            contactPersonFirstName: initialContact?.firstName ?? '',
            contactPersonLastName: initialContact?.lastName ?? '',
            contactPersonPhone: '',
            contactPersonEmail: initialContact?.email ?? '',
            isPrimary: initialContact?.isPrimary ?? false,
            contactPersonImage: initialContact?.profilePictureUrl ?? null
        }
    });
    useEffect(() => {
        if (open) {
            reset({
                contactPersonFirstName: initialContact?.firstName ?? '',
                contactPersonLastName: initialContact?.lastName ?? '',
                contactPersonEmail: initialContact?.email ?? '',
                contactPersonPhone: initialContact?.phoneNo ?? '',
                isPrimary: initialContact?.isPrimary ?? false,
                contactPersonImage: initialContact?.profilePictureUrl ?? null
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialContact]);
    const onSubmit = async (data) => {
        // Permission check as safety measure
        if (!canUpdatePartners) {
            toast.error(t('common.unauthorized') || 'You do not have permission to update partners');
            return;
        }
        const base = {
            firstName: (data.contactPersonFirstName || '').trim(),
            lastName: (data.contactPersonLastName || '').trim(),
            name: `${(data.contactPersonFirstName || '').trim()} ${(data.contactPersonLastName || '').trim()}`.trim(),
            phoneNo: data.contactPersonPhone || '',
            email: (data.contactPersonEmail || '').trim(),
            isPrimary: !!data.isPrimary
        };
        try {
            if (isEdit && initialContact?.id) {
                const request = {
                    id: initialContact.id,
                    ...base,
                    profilePicture: typeof data.contactPersonImage === 'object' ? data.contactPersonImage : null
                };
                await dispatch(updatePartnerContactPersonAsync({ partnerId, request })).unwrap();
            }
            else {
                const request = {
                    ...base,
                    profilePicture: typeof data.contactPersonImage === 'object' ? data.contactPersonImage : null
                };
                await dispatch(createPartnerContactPersonAsync({ partnerId, request })).unwrap();
            }
            onClose();
            reset();
        }
        catch {
            // Toast handled in thunk
        }
    };
    const actionButtons = (_jsxs(Box, { sx: { display: 'flex', gap: '12px', justifyContent: 'center', width: '100%' }, children: [_jsx(Button, { size: 'large', variant: 'outlined', color: 'inherit', fullWidth: true, onClick: onClose, children: t('actions.cancel') }), _jsx(Button, { size: 'large', variant: 'contained', color: 'inherit', fullWidth: true, disabled: !canUpdatePartners, endIcon: _jsx(Icon, { icon: 'eva:checkmark-fill' }), onClick: handleSubmit(onSubmit), children: isEdit ? t('actions.save') : t('actions.addContactPerson') })] }));
    const dialogContent = (_jsx(Box, { sx: {
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            px: { xs: 0, md: 2 },
            pb: { xs: 0, md: 0 }
        }, children: _jsx("form", { onSubmit: handleSubmit(onSubmit), children: _jsxs(Grid, { container: true, spacing: 1.5, children: [_jsx(Grid, { size: { xs: 12 }, children: _jsx(Box, { display: 'flex', justifyContent: 'center', alignItems: 'center', children: _jsx(ImageUpload, { centerVariant: true, name: 'contactPersonImage', control: control, label: t('sponsorships.uploadPicture') }) }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(ControlledFilledTextField, { bg: 'grey8', label: t('sponsorships.contactPersonFirstName'), control: control, name: 'contactPersonFirstName', rules: {
                                required: t('validations.required', {
                                    field: t('sponsorships.contactPersonFirstName')
                                })
                            } }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(ControlledFilledTextField, { bg: 'grey8', label: t('sponsorships.contactPersonLastName'), control: control, name: 'contactPersonLastName', rules: {
                                required: t('validations.required', {
                                    field: t('sponsorships.contactPersonLastName')
                                })
                            } }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(ControlledFilledTextField, { bg: 'grey8', label: t('sponsorships.email'), control: control, name: 'contactPersonEmail', rules: {
                                required: t('validations.required', {
                                    field: t('sponsorships.email')
                                })
                            } }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(PhoneInputMasking, { bg: 'grey8', name: 'contactPersonPhone', label: t('sponsorships.contactPersonPhone'), control: control, error: errors.contactPersonPhone?.message, rules: {
                                required: t('validations.required', {
                                    field: t('sponsorships.contactPersonPhone')
                                }),
                                validate: (value) => {
                                    // Accepts only numbers, must be at least 8 digits
                                    const phoneRegex = /^\d{8,}$/;
                                    if (!phoneRegex.test(value || '')) {
                                        return t('validations.invalidPhone', {
                                            field: t('sponsorships.contactPersonPhone')
                                        });
                                    }
                                    return true;
                                }
                            } }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(Controller, { control: control, name: 'isPrimary', render: ({ field }) => (_jsx(FormControlLabel, { control: _jsx(Switch, { checked: !!field.value, onChange: (_, v) => field.onChange(v) }), label: t('sponsorships.isPrimary') })) }) }), isEdit && (_jsx(Grid, { size: { xs: 12 }, children: _jsx(Alert, { severity: 'warning', variant: 'outlined', icon: _jsx(Icon, { icon: 'eva:alert-triangle-fill' }), sx: {
                                color: theme.palette.warning.dark,
                                backgroundColor: alpha(theme.palette.warning.main, 0.08)
                            }, children: t('sponsorships.contactPersonWarning') }) }))] }) }) }));
    return (_jsxs(_Fragment, { children: [!isMobile && (_jsx(CustomDialog, { backgroundVariant: 'white', open: open, onClose: onClose, titleWithIcon: isEdit ? t('sponsorships.editContactPerson') : t('sponsorships.newContactPerson'), titleIcon: '/assets/images/NewUser.svg', maxWidth: 'sm', actions: actionButtons, children: dialogContent })), isMobile && (_jsx(BDrawer, { dialogActions: actionButtons, title: isEdit ? t('sponsorships.editContactPerson') : t('sponsorships.newContactPerson'), anchor: 'bottom', open: open, onClose: onClose, showCloseButton: true, children: _jsx(Box, { sx: { flex: 1, overflow: 'auto' }, children: dialogContent }) }))] }));
};
export default NewContactDialogDrawer;
